import { zodResolver } from '@hookform/resolvers/zod'
import {
  AlertErrorAdapter,
  Button,
  Flex,
  ProgressStepper,
  Text,
  Title,
  notifications
} from '@libs/ui/ds'
import { BaseForm, Controller } from '@libs/ui/form'
import { useOnboardingTranslation } from '@libs/utils/translations'
import { GTM } from '@spa/core/externals/gtm'
import { useTrackSignupProcess } from '@spa/core/externals/segment/methods/track/hooks/useTrackSignupProcess'
import { ActionName } from '@spa/core/externals/segment/methods/track/types'
import { getCurrentPhoneIndicator } from '@spa/scenes/Auth/legacy/SignUpLegacy/AuthPhoneInput/getCurrentPhoneIndicator'
import type { FC } from 'react'
import { useForm } from 'react-hook-form'
import type { SubmitHandler } from 'react-hook-form'

import { useAccountCreation } from '../../../hooks/useAccountCreation'

import { useProfileQualificationSchema } from './AccountUserInfoForm.validations'
import type { AccountUserInfoFormType } from './AccountUserInfoForm.validations'
import { JobTitleSelect } from './components/JobTitleSelect/JobTitleSelect'

export const AccountUserInfoForm: FC = () => {
  const { t, tCommon } = useOnboardingTranslation()

  const profileQualificationSchema = useProfileQualificationSchema()
  const { profile, handleProfileComplete, updateDraftData } =
    useAccountCreation()
  const { trackSubmitClicked } = useTrackSignupProcess()
  const { email } = useAccountCreation()

  const methods = useForm<AccountUserInfoFormType>({
    mode: 'onBlur',
    resolver: zodResolver(profileQualificationSchema),
    defaultValues: {
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      companyName: profile?.companyName || '',
      jobTitle: profile?.jobTitle || '',
      phone: {
        countryCode:
          profile?.phone?.countryCode ||
          (navigator?.language
            ? new Intl.Locale(navigator.language).region
            : 'FR'),
        number: profile?.phone?.number || ''
      }
    }
  })

  const {
    control,
    formState: { errors, isSubmitting },
    setError
  } = methods

  const onSubmitHandler: SubmitHandler<
    AccountUserInfoFormType
  > = async formData => {
    try {
      const infoData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        jobTitle: formData.jobTitle,
        phone: {
          countryCode: formData.phone.countryCode,
          number: formData.phone.number
        }
      }

      await updateDraftData(infoData)

      trackSubmitClicked({
        actionName: ActionName.SIGNUP_PROGRESS_STEP_2,
        email: email ?? '',
        ...infoData
      })

      GTM.trackIntermediateStep({
        firstname: infoData.firstName,
        lastname: infoData.lastName,
        phone_number: infoData.phone.number,
        email: email ?? '',
        phone_indicator: getCurrentPhoneIndicator(
          infoData.phone.countryCode.toLocaleLowerCase()
        )
      })
      await handleProfileComplete()
    } catch (error) {
      notifications.error({
        message: t('onboarding.qualification.error.draft')
      })
      setError('root', {
        type: 'manual',
        message: t('onboarding.qualification.error.draft')
      })
    }
  }

  const formError = errors.root
    ? {
        message: errors.root.message || ''
      }
    : null

  return (
    <Flex className='w-full'>
      <ProgressStepper
        currentStep={1}
        totalSteps={2}
        color='primary'
        className='mb-10'
      />
      <Flex className='mb-10 gap-2'>
        <Title size={2}>{t('onboarding.qualification.title')}</Title>
        <Text className='text-s'>{t('onboarding.qualification.subtitle')}</Text>
      </Flex>

      <BaseForm methods={methods} onSubmit={onSubmitHandler} role='form'>
        <Flex className='flex-col gap-4'>
          <AlertErrorAdapter error={formError} data-testid='alert-form-error' />
          <Flex className='flex-col gap-4 md:flex-row'>
            <Controller.TextInput
              data-testid='signup-user-firstname-input'
              name='firstName'
              control={control}
              required
              label={t('onboarding.common.forms.firstName')}
              errorMessage={errors?.firstName?.message}
              autoComplete='given-name'
              className='flex-1'
            />

            <Controller.TextInput
              data-testid='signup-user-lastname-input'
              name='lastName'
              control={control}
              required
              label={t('onboarding.common.forms.lastName')}
              errorMessage={errors?.lastName?.message}
              autoComplete='family-name'
              className='flex-1'
            />
          </Flex>

          <Controller.TextInput
            data-testid='signup-user-company-input'
            name='companyName'
            control={control}
            required
            label={t('onboarding.common.forms.companyName')}
            errorMessage={errors?.companyName?.message}
            autoComplete='organization'
          />

          <JobTitleSelect
            control={control}
            name='jobTitle'
            errorMessage={errors?.jobTitle?.message}
          />

          <Controller.PhoneInput
            data-testid='signup-user-phonenumber-input'
            control={control}
            name='phone'
            label={t('onboarding.common.forms.phoneNumber')}
            errorMessage={errors?.phone?.message}
            required
          />

          <Flex className='mt-4'>
            <Button
              type='submit'
              data-testid='cta-account-creation-user-info'
              loading={isSubmitting}
              fillWidth
            >
              {tCommon('action.next')}
            </Button>
          </Flex>
        </Flex>
      </BaseForm>
    </Flex>
  )
}
